/**
 * Translate NOAA-jargon alert titles/messages into a laymans-terms
 * explanation. Uses pattern matching on the alert message string. Returns
 * a short 1-2 sentence explanation focused on how the average human might
 * feel or what it means practically.
 *
 * Kept as a plain function (no React) so it can be unit-tested easily and
 * reused wherever alerts are rendered (Dashboard cards, tooltip content).
 */

const PATTERNS = [
  {
    // X-ray / solar flare events (M-class or X-class)
    test: /(X.?RAY|SOLAR FLARE|FLARE|XRA)/i,
    // Further distinguish class from message
    layman: (msg) => {
      const cls = /(X\d+(?:\.\d+)?|M\d+(?:\.\d+)?)/i.exec(msg);
      if (cls && cls[1][0].toUpperCase() === "X") {
        return "The Sun let off a big X-class flare — like a huge camera flash of radiation. Radio and GPS can flicker for hours, and sensitive people often report fuzzy focus or a low buzzy anxiety.";
      }
      if (cls && cls[1][0].toUpperCase() === "M") {
        return "A medium-strength solar flare has fired. It rarely disrupts daily life, but some folks feel more wired than usual — expect vivid dreams tonight.";
      }
      return "The Sun released a burst of X-ray energy. Everyday life continues, but shortwave radio and GPS may glitch, and empathic people often feel unusually alert.";
    },
  },
  {
    test: /(CORONAL MASS EJECTION|\bCME\b)/i,
    layman: () =>
      "A giant plume of solar plasma is heading our way. It won't harm anything on the ground, but expect a few days of vivid auroras, sharper intuition, and maybe restless sleep.",
  },
  {
    test: /(GEOMAGNETIC STORM|\bG[1-5]\b|K-INDEX|KP)/i,
    layman: (msg) => {
      const g = /\bG([1-5])\b/i.exec(msg);
      const k = /(?:K-index|Kp)[^\d]*(\d)/i.exec(msg);
      const level = g ? parseInt(g[1]) : (k ? Math.max(1, parseInt(k[1]) - 4) : 1);
      if (level >= 4) return "Earth's magnetic field is getting hit hard — a G4 or G5 storm. Migraines, sleep disruption and heightened emotions are common. Aurora may be visible far south of usual.";
      if (level === 3) return "A moderate-strong geomagnetic storm — G3 level. Sensitive nervous systems often feel jumpy; expect a heavier head or shorter fuse today.";
      if (level === 2) return "Earth's magnetic field is being gently shaken (G2). You might feel a bit foggier or wake in the night — nothing dramatic, but ground yourself.";
      return "Earth's magnetic field is a little unsettled — a mild geomagnetic disturbance. Empaths may notice a low-hum unease.";
    },
  },
  {
    test: /(SOLAR RADIATION STORM|PROTON EVENT|RADIATION)/i,
    layman: () =>
      "A shower of high-energy particles from the Sun is arriving. Mostly it affects satellites and polar flights — but some sensitives report jittery focus for a day or two.",
  },
  {
    test: /(RADIO BLACKOUT|R[1-5]\b)/i,
    layman: () =>
      "Shortwave and aviation radios may be patchy for a few hours because a solar flare is ionising Earth's upper atmosphere. GPS accuracy dips a little too.",
  },
  {
    test: /(SUDDEN IMPULSE|SI|SUDDEN COMMENCEMENT)/i,
    layman: () =>
      "Earth's magnetic field just took a sharp jolt — usually the leading edge of a solar wind burst. Sensitive folks often feel a sudden mood-shift right around now.",
  },
  {
    test: /(HIGH SPEED STREAM|SOLAR WIND STREAM|CORONAL HOLE)/i,
    layman: () =>
      "A fast river of solar wind from a coronal hole is streaming past Earth. Auroras may show, sleep can feel lighter, and dreams sharper for the next 2-4 days.",
  },
  {
    test: /(F10\.7|10\.7|RADIO FLUX)/i,
    layman: () =>
      "This measures how loud the Sun's radio hum is today. Higher numbers mean an active Sun overall — a background metric more than an event.",
  },
  {
    test: /(WATCH|CANCEL|CANCELLATION)/i,
    layman: () =>
      "This is a heads-up (or an all-clear) rather than an active event. No immediate impact — the space-weather team is just keeping the field notified.",
  },
];

const DEFAULT_LAYMAN =
  "A space-weather notice from NOAA. Nothing an ordinary day requires you to worry about — but sensitive people sometimes track their moods against these.";

export function laymanExplanation(alert) {
  if (!alert) return DEFAULT_LAYMAN;
  const text = `${alert.title || ""} ${alert.message || ""}`;
  for (const p of PATTERNS) {
    if (p.test.test(text)) {
      return p.layman(text);
    }
  }
  return DEFAULT_LAYMAN;
}

export default laymanExplanation;
